import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Banknote } from 'lucide-react';
import { useTheme } from '../config/theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PaymentMethodPicker
 * -------------------
 * Shows all available payment methods in one unified, consistently-styled list:
 *   • Cash on Delivery (if `includeCod`)
 *   • Online providers fetched from `GET /api/customer/payment/methods`
 *
 * The parent passes a single `onConfirm({ method, provider })` callback and
 * handles creating the order. For COD, `method = 'cash_on_delivery'`.
 * For gateways, `method = 'online'` and `provider` is the gateway code.
 *
 * Dark-mode aware: every card uses theme-aware surfaces so dark pages never
 * show accidental white boxes.
 */
export default function PaymentMethodPicker({
  amount,
  currency = 'AED',
  isAr = true,
  disabled = false,
  includeCod = true,
  onConfirm,
}) {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  // selected is either 'cod' or `provider:<code>`
  const [selected, setSelected] = useState(includeCod ? 'cod' : null);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await axios.get(`${BACKEND_URL}/api/customer/payment/methods`);
        if (!alive) return;
        const list = res.data?.providers || [];
        setProviders(list);
      } catch (e) {
        if (!alive) return;
        const code = e.response?.status;
        setError(
          isAr
            ? `تعذّر جلب طرق الدفع${code ? ` (رمز ${code})` : ''}. يرجى المحاولة لاحقاً أو التواصل مع الدعم.`
            : `Failed to load payment methods${code ? ` (code ${code})` : ''}.`,
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isAr, reloadKey]);

  const feeAmount = (p) => (p.fee_enabled && p.fee_percent ? (amount * p.fee_percent) / 100 : 0);
  const totalWithFee = (p) => (amount || 0) + feeAmount(p);

  // Theme-aware styles ----------------------------------------------------
  const cardBase = isDarkMode
    ? 'bg-[#141414] border-white/10 hover:border-white/20'
    : 'bg-white border-gray-200 hover:border-[#94A3B8]';
  const cardSelected = isDarkMode
    ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 ring-2 ring-[#1E9BD9]/40'
    : 'border-[#1E9BD9] bg-[#1E9BD9]/5 ring-2 ring-[#1E9BD9]/30';
  const titleText = isDarkMode ? 'text-white' : 'text-[#0F172A]';
  const subText = isDarkMode ? 'text-gray-400' : 'text-[#64748B]';
  const totalText = isDarkMode ? 'text-white' : 'text-[#0F172A]';
  const iconBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-10 ${subText}`} data-testid="payment-picker-loading">
        <Loader2 className="h-5 w-5 animate-spin text-[#1E9BD9]" />
        <span className="ms-2">{isAr ? 'جاري تحميل طرق الدفع…' : 'Loading payment methods…'}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`p-4 rounded-xl border space-y-3 ${isDarkMode ? 'border-red-500/40 bg-red-500/10' : 'border-red-200 bg-red-50'}`}
        data-testid="payment-picker-error"
      >
        <p className={isDarkMode ? 'text-red-300 text-sm' : 'text-red-700 text-sm'}>{error}</p>
        <button
          type="button"
          data-testid="payment-picker-retry"
          onClick={() => setReloadKey((k) => k + 1)}
          className="w-full py-2 rounded-xl bg-red-600 text-white font-medium hover:opacity-90"
        >
          {isAr ? 'إعادة المحاولة' : 'Retry'}
        </button>
      </div>
    );
  }

  if (!includeCod && !providers.length) {
    return (
      <div className={`p-4 rounded-xl border text-sm ${
        isDarkMode ? 'border-amber-500/30 bg-amber-500/10 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-800'
      }`}>
        {isAr ? 'لا توجد طريقة دفع مفعّلة حالياً.' : 'No payment method is currently available.'}
      </div>
    );
  }

  const renderCard = ({ key, isSelected, onClick, cardDisabled, icon, title, description, right, badge }) => (
    <button
      key={key}
      type="button"
      data-testid={`payment-method-${key}`}
      disabled={cardDisabled}
      onClick={onClick}
      className={`w-full text-start p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${
        isSelected ? cardSelected : cardBase
      } ${cardDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-0.5'}`}
    >
      <div className={`w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-bold ${titleText}`}>{title}</span>
          {badge}
        </div>
        {description && <div className={`text-xs mt-1 truncate ${subText}`}>{description}</div>}
      </div>
      {right}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="payment-picker">
      {/* Cash on Delivery */}
      {includeCod && renderCard({
        key: 'cod',
        isSelected: selected === 'cod',
        onClick: () => setSelected('cod'),
        cardDisabled: disabled,
        icon: <Banknote className="h-7 w-7 text-emerald-500" />,
        title: isAr ? 'الدفع عند الاستلام' : 'Cash on Delivery',
        description: isAr ? 'ادفع نقداً عند استلام طلبك' : 'Pay cash when you receive your order',
        right: amount != null && (
          <div className="text-end shrink-0">
            <div className={`text-xs ${subText}`}>{isAr ? 'الإجمالي' : 'Total'}</div>
            <div className={`font-bold ${totalText}`}>
              {Number(amount).toFixed(2)} <span className="text-xs">{currency}</span>
            </div>
          </div>
        ),
      })}

      {/* Online gateways */}
      {providers.map((p) => {
        const key = `provider:${p.provider}`;
        const isSelected = selected === key;
        const total = totalWithFee(p);
        const belowMin = amount != null && p.min_amount != null && amount < p.min_amount;
        const aboveMax = amount != null && p.max_amount != null && amount > p.max_amount;
        const cardDisabled = disabled || belowMin || aboveMax;

        return renderCard({
          key: p.provider,
          isSelected,
          onClick: () => setSelected(key),
          cardDisabled,
          icon: p.logo_url
            ? <img src={p.logo_url} alt={p.display_name_en || p.provider} className="w-full h-full object-contain p-2" />
            : <span className="text-lg font-bold" style={{ color: p.color || '#0F172A' }}>
                {(p.display_name_en || p.provider).slice(0, 2).toUpperCase()}
              </span>,
          title: isAr ? (p.display_name_ar || p.display_name_en) : (p.display_name_en || p.provider),
          description: isAr ? (p.description_ar || '') : (p.description_en || ''),
          badge: (
            <>
              {p.fee_enabled && p.fee_percent > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">+{p.fee_percent}%</span>
              )}
              {belowMin && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                  {isAr ? `حد أدنى ${p.min_amount}` : `Min ${p.min_amount}`}
                </span>
              )}
              {aboveMax && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                  {isAr ? `حد أعلى ${p.max_amount}` : `Max ${p.max_amount}`}
                </span>
              )}
            </>
          ),
          right: amount != null && !belowMin && !aboveMax && (
            <div className="text-end shrink-0">
              <div className={`text-xs ${subText}`}>{isAr ? 'الإجمالي' : 'Total'}</div>
              <div className={`font-bold ${totalText}`}>
                {total.toFixed(2)} <span className="text-xs">{currency}</span>
              </div>
            </div>
          ),
        });
      })}

      <button
        type="button"
        data-testid="payment-picker-confirm"
        disabled={!selected || disabled}
        onClick={() => {
          if (!selected) return;
          if (selected === 'cod') onConfirm?.({ method: 'cash_on_delivery' });
          else onConfirm?.({ method: 'online', provider: selected.replace('provider:', '') });
        }}
        className="w-full mt-2 h-12 rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0EA5E9] text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-95"
      >
        {isAr ? 'تأكيد وإتمام الدفع' : 'Confirm & continue'}
      </button>
    </div>
  );
}
